import { Injectable } from '@angular/core';
import defaults from 'lodash-es/defaults';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import each from 'lodash-es/each';
import { TimeoutService } from '@df/ng/timeout.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { Resource } from '@df/catalog/resource';
import { Model } from '@df/catalog/model';

export interface ILoaderSettings<T extends Model> {
    type: string;
    pageSize: number;
    verbosity: number;
    noCache?: boolean;
    getFilter: (query?: string) => any;
    getSort?: () => any;
    onModels: (models: T[]) => Promise<void>;
    onStateChange?: (state: ILoaderState) => void;
}

export interface ILoaderState {
    canLoadMore: boolean;
    isFullyLoaded: boolean;
}

@Injectable()
export class CatalogLazyLoader<T extends Model> {
    readonly searchStatus = new Status();

    searchQuery?: string;
    private searchQueryApplied?: string;

    private loadedModels: T[] = [];
    private state: ILoaderState = {
        canLoadMore: true,
        isFullyLoaded: false
    };

    private invalidQty = 0;
    private maxPage = 0;

    private loaderSettings!: ILoaderSettings<T>;

    constructor(
        private app: App,
        private timeoutService: TimeoutService
    ) {}

    init(loaderOptions: ILoaderSettings<T>) {
        this.loaderSettings = loaderOptions;
        this.loaderSettings.onStateChange && this.loaderSettings.onStateChange(this.state);
        this.loadMore();
    }

    // async reload() {
    //     await this.load(this.maxPage - 1);
    // }

    async loadMore(page = this.maxPage + 1) {
        if (!this.state.canLoadMore) {
            return;
        }
        return this.load(page);
    }

    trackFn(item: T) {
        return item.id;
    }

    private get resource() {
        return this.app.resources[this.loaderSettings.type] as any as Resource<T>;
    }

    private async load(page = this.maxPage + 1) {
        this.updateState({
            canLoadMore: false
        });

        const query = this.searchQueryApplied;
        const models = await this.resource.find(
            {
                verbosity: this.loaderSettings.verbosity,
                filter: this.loaderSettings.getFilter(query),
                sort: this.loaderSettings.getSort && this.loaderSettings.getSort(),
                skip: (page - 1) * this.loaderSettings.pageSize - this.invalidQty,
                limit: this.loaderSettings.pageSize + this.invalidQty
            },
            { noCache: this.loaderSettings.noCache }
        );

        if (this.searchQueryApplied !== query) {
            return;
        }

        if (!models) {
            this.updateState({
                canLoadMore: !this.state.isFullyLoaded
            });
            return;
        }

        if (models.length) {
            this.loadedModels = uniq([...this.loadedModels, ...models]);
            this.maxPage = page;
            await this.onLoadedModelsChange();
            this.updateState({
                canLoadMore: true,
                isFullyLoaded: false
            });
        } else {
            this.updateState({
                canLoadMore: false,
                isFullyLoaded: true
            });
        }
    }

    private async onLoadedModelsChange() {
        const items: T[] = [];
        let invalidQty = 0;
        each(this.loadedModels, model => {
            if (model.data) {
                items.push(model);
            } else {
                invalidQty++;
            }
        });
        await this.loaderSettings.onModels(items);
        this.invalidQty = invalidQty;
    }

    async search(force = false) {
        let query = this.searchQuery?.trim() || '';
        if (query.length < 3) {
            query = '';
        }

        if (!force && this.searchQueryApplied === query) {
            return;
        }
        // reset search
        if (!query) {
            this.reset('');
            await this.loadMore();
        }
        // search
        else if (query.length > 1) {
            this.searchStatus.busy = true;
            this.reset(query);
            await this.loadMore();
            this.searchStatus.busy = false;
        }
    }

    private reset(query: string) {
        this.searchQueryApplied = query;
        this.loadedModels = [];
        this.onLoadedModelsChange();
        this.maxPage = 0;
        this.updateState({
            isFullyLoaded: false,
            canLoadMore: true
        });
    }

    private updateState(state: Partial<ILoaderState>) {
        const _state = defaults(state, this.state);
        if (!isEqual(_state, this.state)) {
            this.state = _state;
            if (this.loaderSettings.onStateChange) {
                this.loaderSettings.onStateChange(this.state);
            }
        }
    }
}
